import { PAApiServices } from "./pa-api-services";
import { PARegistry } from "../singletons/pa-registry";
import type { Priority } from "src/app/_models/priority.interface"
import type { Status } from "src/app/_models/status.interface"
import type { ProjectService } from "src/app/_services"
import { PAProgress } from "./progress"
import { PAStore } from "./store"
import type { PlanningAssistantService } from "src/app/_services/planning-assistant.service"
import { PAUtil } from "./util"
import { PAOperation } from "./operation";
import type { OperationCoordinates } from "../../../_models/operation.interface";
import type { TicketCoordinates } from "../../../_models/ticket.interface";

export class PAProject {

  static projectService: ProjectService
  static planningAssistantService: PlanningAssistantService

  operationLoadingStatus: 'init' | 'loading' | 'loaded' = 'init'
  ticketLoadingStatus: 'init' | 'loading' | 'loaded' = 'init'

  constructor(
    public id: number,
    public project_name: string,
    public customer_id: number,
    public active_technician_ids: number[],
    public experienced_technician_ids: number[],
    public color: string,
    public priorities: Priority[],
    public status: Status[]
  ) {
  }

  public updateColor(event: Event): void {
    const input = event.target as HTMLInputElement
    const color: string = input.value
    
    this.updateColorMeta(color)
  }

  public updateColorMeta(color: string): void {    
    const params = {color: color}
    PAProject.projectService.updateProject(this.id.toString(), params).subscribe(
      (_) => {
        this.color = color
        const project_operations = PARegistry.dataControl.getChangedLoadedOperations().filter(operation => operation.ticket.project.id == this.id)
        for (const operation of project_operations) {
          const store = PAStore.getOperationsStore(operation)
          if (store) {
            store.color = PAUtil.hexToRgba(color)
          }
          const tour = operation.getTour()
          if (tour) {
            tour.updateRoute()
          }
        }
      },
      (error) => {
        console.log(error)
      }
    )
  }

  // Ziel-Status beim Einplanen eines Auftrags. Primär über die Status-Flags:
  // erster Status (Liste ist name-sortiert), der weder unplanned noch
  // closed/canceled ist — z.B. "3.0 geplant" (vor "4.0 QM"). Das frühere reine
  // Namens-Matching bleibt nur als Fallback für Projekte ohne gepflegte Flags:
  // es traf fälschlich auch "2.1 vorläufig geplant" (enthält "geplant") und
  // wählte damit je nach Sortierung den falschen Status.
  public getPlannedStatus(): Status {
    // Guard (hawk#558, Aufgabe A): Die Flag-Wahl nur nutzen, wenn das Projekt
    // die Flags tatsächlich pflegt (mindestens ein Status ist unplanned).
    // unplanned ist NOT NULL DEFAULT false — „kein Flag gepflegt" und
    // „explizit als geplant markiert" sind am Einzelstatus also
    // ununterscheidbar. Ohne den Guard träfe der Flag-Zweig in Projekten ohne
    // „1.x/2.x"-Konvention immer den alphabetisch ersten offenen Status
    // (z. B. „angenommen" vor „geplant"), und der bewährte Namens-Fallback
    // würde nie mehr erreicht.
    const flagsMaintained = this.status.some(status => status.unplanned)
    if (flagsMaintained) {
      const flag_based = this.status.find(status => status.unplanned === false && !status.closed && !status.canceled)
      if (flag_based) return flag_based
    }

    const name_based = this.status.filter(status => status.name.toLowerCase().includes('geplant') && !status.name.toLowerCase().includes('ungeplant'))
    return name_based.length ? name_based[0] : null
  }

  // Ziel-Status beim Ausplanen (Auftrag ohne Datum/Techniker). Bevorzugt den
  // als fallback_status markierten Status des Projekts (hawk#549: Zielstatus
  // beim Zurücksetzen, z.B. "2.0 ungeplant"). Fallback: Namens-Matching wie
  // bisher — NICHT "erster Status mit unplanned-Flag", denn auch 1.x-Status
  // tragen das Flag und stünden name-sortiert vor "2.0 ungeplant".
  public getUnplannedStatus(): Status {
    const fallback_flagged = this.status.find(status => status.fallback_status && !status.closed && !status.canceled)
    if (fallback_flagged) return fallback_flagged

    const name_based = this.status.filter(status => status.name.toLowerCase().includes('ungeplant'))
    return name_based.length ? name_based[0] : null
  }

  public getStatus(status_id: number): Status {
    const possible_status = this.status.filter(status => status.id == status_id)
    if (possible_status.length > 0) {
      return possible_status[0]
    }
    console.log("No status  with id: " + status_id + " found")
  }

  public includesString(s: string): boolean {
    return `${this.id} ${this.project_name}`.toLowerCase().includes(s.toLowerCase())
  }

  public excludesString(s: string): boolean {
    return !`${this.id} ${this.project_name}`.toLowerCase().includes(s.toLowerCase())
  }

  public isServiceProject(): boolean {
    return this.includesString('service')
  }

  public isNotServiceProject(): boolean {
    return this.excludesString('service')
  }

  public async loadUnplannedOperationsForProject(): Promise<void> {
    if (!['loading', 'loaded'].includes(this.operationLoadingStatus)) {
      this.operationLoadingStatus = 'loading'
      const prio_progress = new PAProgress('Priorisiere Aufträge aus: ' + this.project_name, 1)

      const operation_coordinates = await new Promise<OperationCoordinates[]>(resolve => {
        prio_progress.addSubProgress(PAApiServices.operationService.loadOperationCoordinates({
          project_id: this.id,
          exclude_material_return: true,
          exclude_staging: true,
          exclude_material_shipping: true,
          exclude_pickup_orders: true,
          only_unassigned: true
        }), '').subscribe(
          data => {
            resolve(data)
          },
          err => {
            console.log(err)
            resolve([])
          }
        )
      })

      if (operation_coordinates.length) {
        const distance_sorted_coordinates = operation_coordinates.sort(
          (c1: OperationCoordinates, c2: OperationCoordinates) => {
            const loading_coordinates = PARegistry.dataControl.loadingCoordinates
            return PAUtil.calcDistanceAsTheCrowFlies(c1.latitude, c1.longitude, loading_coordinates.lat, loading_coordinates.lng) < PAUtil.calcDistanceAsTheCrowFlies(c2.latitude, c2.longitude, loading_coordinates.lat, loading_coordinates.lng) ? -1 : 1
          }
        )

        const chunk_size = 250
        const coordinates_chunks = PAUtil.chunkArray<OperationCoordinates>(distance_sorted_coordinates, chunk_size)
        const loading_progress = new PAProgress(`Lade ${operation_coordinates.length} Aufträge (${this.project_name})`, coordinates_chunks.length)

        for (const coordinates_chunk of coordinates_chunks) {
          await new Promise<void>(resolve => {
            loading_progress.addSubProgress(PAProject.planningAssistantService.getPlanningAssistantOperations(coordinates_chunk.map(op_chunk => op_chunk.operation_id)), `${coordinates_chunks.indexOf(coordinates_chunk) * chunk_size + 1} - ${Math.min((coordinates_chunks.indexOf(coordinates_chunk) + 1) * chunk_size, operation_coordinates.length)}`).subscribe(
              data => {
                if (data.length) {
                  PARegistry.dataControl.processOperationHashes(data)
                }
                resolve()
              },
              error => {
                console.log(error)
                resolve()
              }
            )
          })
        }
      }

      this.operationLoadingStatus = 'loaded'
    } else {
      return
    }
  }

  public async loadUnplannedTicketsForProject(): Promise<void> {
      if (!['loading', 'loaded'].includes(this.ticketLoadingStatus)) {
        this.ticketLoadingStatus = 'loading'

        const prio_progress = new PAProgress('Priorisiere Tickets aus: ' + this.project_name, 1)

        let ticket_coordinates = await new Promise<TicketCoordinates[]>(resolve => {
          prio_progress.addSubProgress(PAApiServices.ticketService.loadTicketCoordinates({
            project_id: this.id,
            exclude_material_return: true,
            exclude_staging: true,
            exclude_material_shipping: true,
            only_without_operations: true
          }), '').subscribe(
            data => {
              resolve(data)
            },
            err => {
              console.log(err)
              resolve([])
            }
          )
        })

        const loaded_route_ticket_id = PARegistry.dataControl.loadedRouteTicketId
        if (loaded_route_ticket_id) ticket_coordinates = ticket_coordinates.filter(t => t.ticket_id != loaded_route_ticket_id)

        if (ticket_coordinates.length) {
          const distance_sorted_coordinates = ticket_coordinates.sort(
            (c1: TicketCoordinates, c2: TicketCoordinates) => {
              const loading_coordinates = PARegistry.dataControl.loadingCoordinates
              return PAUtil.calcDistanceAsTheCrowFlies(c1.latitude, c1.longitude, loading_coordinates.lat, loading_coordinates.lng) < PAUtil.calcDistanceAsTheCrowFlies(c2.latitude, c2.longitude, loading_coordinates.lat, loading_coordinates.lng) ? -1 : 1
            }
          )

          const chunk_size = 250
          const coordinates_chunks = PAUtil.chunkArray<TicketCoordinates>(distance_sorted_coordinates, chunk_size)
          const loading_progress = new PAProgress(`Lade ${ticket_coordinates.length} Tickets (${this.project_name})`, coordinates_chunks.length)

          for (const coordinates_chunk of coordinates_chunks) {
            await new Promise<void>(resolve => {
              loading_progress.addSubProgress(PAProject.planningAssistantService.getPlanningAssistantTickets(coordinates_chunk.map(op_chunk => op_chunk.ticket_id)), `${coordinates_chunks.indexOf(coordinates_chunk) * chunk_size + 1} - ${Math.min((coordinates_chunks.indexOf(coordinates_chunk) + 1) * chunk_size, ticket_coordinates.length)}`).subscribe(
                data => {
                  if (data.length) {
                    const unplanned_tickets = data.map(ticket_hash => PARegistry.dataControl.ticketHashToTicket(ticket_hash, PARegistry.dataControl.generateNextLocationId(), PARegistry.dataControl.generateNextClientId()))
                    for (const ticket of unplanned_tickets) {
                      PAOperation.generateTemporaryTicketOperation(ticket)
                    }
                    PARegistry.filterControl.updateUnassignedOperations()
                    resolve()
                  }
                  resolve()
                },
                error => {
                  console.log(error)
                  resolve()
                }
              )
            })
          }
        }

        this.ticketLoadingStatus = 'loaded'
      } else {
       return
      }
  }

  public hasPriorityWithRule(): boolean {
    return !!this.priorities.find(p => PARegistry.settingsControl.getAdditionalPriorityTimeRules(p).length > 0)
  }

  static setProjectService(service: ProjectService) {
    this.projectService = service
  }

  static setPlanningAssistantService(service: PlanningAssistantService) {
    this.planningAssistantService = service
  }

}